import {
  api,
  type AppInfoWithPrivacyFlags,
  type PrivacyFlagsUpdate,
} from '@/portal-api.definition';
import { queryOptions } from '@tanstack/react-query';
import { useNosIdMutation, useNosIdQuery } from './base.hooks';
import { ActionType, useI18n, useToast } from '@nosinovacao/nosid-mfe-common';

const privacyPoliciesQueryKey = ['privacy-policies'] as const;

export function getPrivacyPoliciesQueryOptions(language: string) {
  return queryOptions({
    queryKey: [...privacyPoliciesQueryKey, language] as const,
    queryFn: () =>
      api
        .get('/api/v1/privacy-policies')
        .then((policies) => policies.filter((policy) => policy.PrivacyFlags.length)),
  });
}

export function useGetPrivacyPolicies() {
  const { language } = useI18n();
  return useNosIdQuery(getPrivacyPoliciesQueryOptions(language));
}

function updatePrivacyFlag(
  flag: AppInfoWithPrivacyFlags['PrivacyFlags'][number],
  inputPrivacyFlags: PrivacyFlagsUpdate['PrivacyFlags'],
) {
  const flagToUpdate = inputPrivacyFlags.find((newFlag) => newFlag.Type === flag.Type);
  return flagToUpdate ? { ...flag, IsAccepted: flagToUpdate.IsAccepted } : flag;
}

function updatePrivacyPolicy(policy: AppInfoWithPrivacyFlags, input: PrivacyFlagsUpdate) {
  if (policy.Client.Id === input.ClientId) {
    return {
      ...policy,
      PrivacyFlags: policy.PrivacyFlags.map((flag) => updatePrivacyFlag(flag, input.PrivacyFlags)),
    };
  }
  return policy;
}

export function useUpdatePrivacyPolicies() {
  const toast = useToast();
  const { t } = useI18n();
  return useNosIdMutation({
    mutationFn: (privacyPolicies: PrivacyFlagsUpdate) =>
      api.put('/api/v1/privacy-policies', privacyPolicies),
    onSuccess: (_result, input, _variables, { client }) => {
      client.setQueriesData<AppInfoWithPrivacyFlags[]>(
        { queryKey: privacyPoliciesQueryKey },
        (currentData) => currentData?.map((p) => updatePrivacyPolicy(p, input)),
      );

      toast.show('success', t('Common.SnackBar.PrivacyPolicyUpdated'));
    },
    successLogAction: (input) => ({
      ActionType: ActionType.UpdateConsents,
      Option: input.ClientId,
    }),
    errorLogAction: (input) => ({
      ActionType: ActionType.UpdateConsents,
      Option: input.ClientId,
    }),
  });
}
